// frontend/src/lib/coaImport.ts
import type { CoaAccount as BaseCoaAccount, CoaAccountType } from '../components/CoaPreview';

export const MAX_IMPORT_ROWS = 500;
const MAX_BALANCE = 999_999_999_999;
const PLACEHOLDER_TYPE: Record<string, CoaAccountType> = {
  '1': 'asset', '2': 'liability', '3': 'equity', '4': 'revenue',
  '5': 'cost', '6': 'expense', '7': 'expense', '8': 'expense',
};

export interface CoaAccount extends BaseCoaAccount {
  opening_balance?: number;
}

export interface RawCoaRow { rowIndex: number; code: string; name: string; balance?: number; }

/** Error-backed so array.join() surfaces messages (viewer code uses row/message fields). */
export class ImportIssue extends Error {
  row: number | null;
  constructor(row: number | null, message: string) {
    super(message);
    this.row = row;
    this.name = 'ImportIssue';
  }
  toString(): string { return this.message; }
}

export interface CoaImportResult { accounts: CoaAccount[]; errors: ImportIssue[]; warnings: ImportIssue[]; }

const issue = (row: number | null, message: string): ImportIssue => new ImportIssue(row, message);

export function cleanCode(raw: unknown): string {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '');
  return digits === '' ? '' : String(parseInt(digits, 10));
}

/** Parse an opening-balance cell, treating accounting notation as negative:
 *  "(50,000)" and "12000-" both yield -50000 / -12000. Non-numeric text → NaN. */
export function parseBalanceCell(raw: unknown): number {
  if (typeof raw === 'number') return raw;
  let s = String(raw).trim().replace(/,/g, '');
  let negative = false;
  if (s.length >= 2 && s.startsWith('(') && s.endsWith(')')) {
    negative = true;
    s = s.slice(1, -1).trim();
  } else if (s.endsWith('-')) {
    negative = true;
    s = s.slice(0, -1).trim();
  }
  const n = Number(s);
  return negative ? -n : n;
}

export async function readCoaFile(file: File): Promise<RawCoaRow[]> {
  const XLSX = await import('xlsx');
  const ext = file.name.toLowerCase().split('.').pop() || '';
  const wb = ext === 'csv'
    ? XLSX.read(await file.text(), { type: 'string' })
    : XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  if (!sheet) return [];
  const grid = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, blankrows: false, defval: '' });

  const rows: RawCoaRow[] = [];
  grid.forEach((r: unknown[], idx) => {
    const rawCode = r[0];
    const codeStr = typeof rawCode === 'number' ? String(Math.trunc(rawCode)) : String(rawCode ?? '');
    if (codeStr.replace(/[^0-9]/g, '') === '') return; // blank/header row
    const balanceCell = r[2];
    rows.push({
      rowIndex: idx + 1,
      code: codeStr.replace(/[^0-9]/g, ''),
      name: String(r[1] ?? '').trim(),
      ...(balanceCell !== '' && balanceCell !== undefined && balanceCell !== null
        ? { balance: parseBalanceCell(balanceCell) }
        : {}),
    });
  });
  return rows;
}

function pad5(code: string): string { return code + '0'.repeat(5 - code.length); }

function roundDownParent(code5: string): string | null {
  if (code5.endsWith('0000')) return null;
  if (code5.endsWith('000')) return code5.slice(0, 1) + '0000';
  if (code5.endsWith('00')) return code5.slice(0, 2) + '000';
  return code5.slice(0, 3) + '00';
}

/** Naive stem parent for mismatch detection (warnings only, never used as result). */
function stemParent(code5: string): string | null {
  const stem = code5.replace(/0+$/, '') || code5.slice(0, 1);
  if (stem.length <= 1) return null;
  return pad5(stem.slice(0, -1));
}

export function buildImport(rows: RawCoaRow[]): CoaImportResult {
  const errors: ImportIssue[] = [];
  const warnings: ImportIssue[] = [];
  let accounts = new Map<string, CoaAccount>();

  if (rows.length > MAX_IMPORT_ROWS) {
    errors.push(issue(null, `File exceeds ${MAX_IMPORT_ROWS} accounts`));
  }
  const names = new Set<string>();
  const codes = new Set<string>();
  for (const r of rows) {
    const { rowIndex, name } = r;
    // class digit comes from the raw digit string (before leading-zero stripping):
    // "01000" is a class-0 code, not a class-1 code.
    const rawDigits = String(r.code).replace(/[^0-9]/g, '');
    const code = cleanCode(r.code);
    if (code === '' || code.length > 5) {
      errors.push(issue(rowIndex, `Invalid code "${r.code}" (must be 1-5 digits)`));
      continue;
    }
    if (codes.has(code)) { errors.push(issue(rowIndex, `Duplicate code ${code}`)); continue; }
    if (!name.trim()) { errors.push(issue(rowIndex, `Row ${rowIndex}: missing account name`)); continue; }
    if (names.has(name.trim().toLowerCase())) { errors.push(issue(rowIndex, `Duplicate account name "${name}"`)); continue; }
    const type = PLACEHOLDER_TYPE[rawDigits[0] ?? code[0]];
    if (!type) { errors.push(issue(rowIndex, `Invalid class digit in code "${r.code}"`)); continue; }
    if (r.balance !== undefined && (!Number.isFinite(r.balance) || Math.abs(r.balance) > MAX_BALANCE)) {
      errors.push(issue(rowIndex, `Invalid opening balance "${r.balance}"`));
      continue;
    }
    codes.add(code); names.add(name.trim().toLowerCase());
    accounts.set(code, {
      account_code: code, account_name: name.trim(), account_type: type, parent_code: null,
      ...(r.balance !== undefined ? { opening_balance: r.balance } : {}), is_custom: true,
    });
  }
  if (errors.length > 0) return { accounts: [], errors, warnings };

  const allCodes = [...accounts.keys()];
  const mixed = new Set(allCodes.map(c => c.length)).size > 1;

  // Rebuild into a NEW map over a snapshot (never mutate a Map while iterating it).
  if (mixed) {
    const next = new Map<string, CoaAccount>();
    for (const a of [...accounts.values()]) {
      const prefix = allCodes
        .filter(p => p.length < a.account_code.length && a.account_code.startsWith(p))
        .sort((x, y) => y.length - x.length)[0];
      const padded = pad5(a.account_code);
      if (padded !== a.account_code) {
        warnings.push(issue(null, `Code ${a.account_code} padded to ${padded}`));
      }
      // never self-parent: if the prefix's padded form IS this code, fall back to round-down
      let parent: string | null;
      if (prefix) {
        const prefixPadded = pad5(prefix);
        if (prefixPadded === padded) {
          parent = roundDownParent(padded);
          warnings.push(issue(null, `Code ${padded} staged under itself; parent fell back to ${parent ?? 'root'}`));
        } else {
          parent = prefixPadded;
        }
      } else {
        parent = roundDownParent(padded);
      }
      next.set(padded, { ...a, account_code: padded, parent_code: parent });
    }
    if (next.size < accounts.size) {
      errors.push(issue(null, `Duplicate code after padding: two accounts collapse to the same 5-digit code (padding collision at codes ${[...accounts.keys()].join(', ')})`));
      return { accounts: [], errors, warnings };
    }
    accounts = next;
  } else {
    const next = new Map<string, CoaAccount>();
    for (const a of [...accounts.values()]) {
      const c = pad5(a.account_code);
      if (c !== a.account_code) warnings.push(issue(null, `Code ${a.account_code} padded to ${c}`));
      next.set(c, { ...a, account_code: c, parent_code: roundDownParent(c) });
    }
    accounts = next;
  }

  // parent-reference links to codes that exist; auto-create missing ancestors recursively
  const created: string[] = [];
  for (const a of [...accounts.values()]) {
    let parent: string | null = a.parent_code;
    while (parent && !accounts.has(parent)) {
      const header: CoaAccount = {
        account_code: parent, account_name: `自動 ${parent} Group`,
        account_type: PLACEHOLDER_TYPE[parent[0]], parent_code: roundDownParent(parent), is_custom: true,
      };
      accounts.set(parent, header); created.push(parent);
      parent = header.parent_code;
    }
  }
  if (created.length > 0) {
    warnings.push(issue(null, `Auto-created parent account(s): ${created.join(', ')}`));
  }
  if (accounts.size > MAX_IMPORT_ROWS) {
    errors.push(issue(null, `Import exceeds ${MAX_IMPORT_ROWS} accounts after adding parents`));
    return { accounts: [], errors, warnings };
  }

  // assumption warnings: naive stem parent differing from computed parent (rollover / 5th-level flatten)
  for (const a of [...accounts.values()]) {
    const naive = stemParent(a.account_code);
    if (naive && naive !== a.parent_code) {
      warnings.push(issue(null, `${a.account_code} assumed to be a leaf under ${a.parent_code ?? 'root'}`));
    }
  }

  const sorted = [...accounts.values()].sort((x, y) => x.account_code.localeCompare(y.account_code));
  // imported rows (with their balances) come first; auto-created placeholder headers follow
  const createdSet = new Set(created);
  const list = [...sorted.filter(a => !createdSet.has(a.account_code)), ...sorted.filter(a => createdSet.has(a.account_code))];
  // balances travel with the account object (opening_balance is spread through every remap)
  return { accounts: list.map(a => ({ ...a, is_custom: true })), errors, warnings };
}

export interface CoaTemplateRow { account_code: string; account_name: string; }

function csvField(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Build the downloadable CSV template. When template accounts are supplied,
 * one row is emitted per account (names verbatim, opening balance left blank);
 * otherwise falls back to the built-in example rows. */
export function coaTemplateCsv(accounts?: CoaTemplateRow[]): string {
  const header = [
    '科目編碼,科目名稱,期初結餘',
    'Code,Name,Opening Balance',
  ];
  const rows = accounts && accounts.length > 0
    ? accounts.map(a => `${csvField(a.account_code)},${csvField(a.account_name)},`)
    : ['11101,銀行存款 HSBC,50000', '21101,應付賬款 Trade Creditors,12000'];
  return '\ufeff' + [...header, ...rows].join('\n');
}
